import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  ChangeEvent,
} from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
  limitInputLength,
} from "../../../helpers";
import {
  ChoiceList,
  Dropdown,
  TextField,
  Alert,
} from "@cmsgov/ds-medicare-gov";
import PlanInfo from "./../PlanInfo";
import PhoneNumberInput from "./../PhoneNumberInput";
import states from "./../states";
import { PlanType, PlanCategoryType, PlanSNPType } from "../../../@types";
import {
  isCostPlanWithDrugs,
  isPffsWithDrugs,
  removeExtraWhiteSpace,
} from "../../../helpers/enrollmentFormHelpers";

const GeneralQuestions: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const [isValidForm, setIsValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [renalDiseaseErrorMessage, setRenalDiseaseErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [
    additionalDrugCoverageNameErrorMessage,
    setAdditionalDrugCoverageNameErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    additionalDrugCoverageIdErrorMessage,
    setAdditionalDrugCoverageIdErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    additionalDrugCoverageGroupErrorMessage,
    setAdditionalDrugCoverageGroupErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCareNameErrorMessage,
    setLongTermCareNameErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCareAddressLine1ErrorMessage,
    setLongTermCareAddressLine1ErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCareCityErrorMessage,
    setLongTermCareCityErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCareStateErrorMessage,
    setLongTermCareStateErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCareZipErrorMessage,
    setLongTermCareZipErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    longTermCarePhoneErrorMessage,
    setLongTermCarePhoneErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    enrolledInMedicaidErrorMessage,
    setEnrolledInMedicaidErrorMessage,
  ] = useState<string | undefined>(undefined);
  const alertDiv = useRef<HTMLDivElement>(null);

  const isPDP = plan.plan_type === PlanType.PDP;
  const isCP_PD = isCostPlanWithDrugs(plan);
  const isPFFS_PD = isPffsWithDrugs(plan);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.GeneralQuestions
    );

    setIsValidForm(formStatus.isValid);

    if (submitted) {
      if (formStatus.renalDiseaseQuestionValidity) {
        setRenalDiseaseErrorMessage(undefined);
      } else {
        setRenalDiseaseErrorMessage(
          t("enroll_form.renal_disease_question.error")
        );
      }

      if (enrollData.hasAdditionalDrugCoverage) {
        if (formStatus.additionalDrugCoverageNameValidity) {
          setAdditionalDrugCoverageNameErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageNameErrorMessage(
            t("enroll_form.additional_drug_coverage.name.error")
          );
        }
        if (formStatus.additionalDrugCoverageIdValidity) {
          setAdditionalDrugCoverageIdErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageIdErrorMessage(
            t("enroll_form.additional_drug_coverage.id.error")
          );
        }
        if (formStatus.additionalDrugCoverageGroupValidity) {
          setAdditionalDrugCoverageGroupErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageGroupErrorMessage(
            t("enroll_form.additional_drug_coverage.group.error")
          );
        }
      }

      if (enrollData.inLongTermCareFacility) {
        if (formStatus.longTermCareNameValidity) {
          setLongTermCareNameErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCareNameErrorMessageErrorMessage(
            t("enroll_form.long_term_care_facility.name.error")
          );
        }
        if (formStatus.longTermCareAddressLine1Validity) {
          setLongTermCareAddressLine1ErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCareAddressLine1ErrorMessageErrorMessage(
            t("enroll_form.address.line_1.error")
          );
        }
        if (formStatus.longTermCareCityValidity) {
          setLongTermCareCityErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCareCityErrorMessageErrorMessage(
            t("enroll_form.address.city.error")
          );
        }
        if (formStatus.longTermCareStateValidity) {
          setLongTermCareStateErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCareStateErrorMessageErrorMessage(
            t("enroll_form.address.state.error")
          );
        }
        if (formStatus.longTermCareZipValidity) {
          setLongTermCareZipErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCareZipErrorMessageErrorMessage(
            t("enroll_form.address.zip.error")
          );
        }
        if (formStatus.longTermCarePhoneValidity) {
          setLongTermCarePhoneErrorMessageErrorMessage(undefined);
        } else {
          setLongTermCarePhoneErrorMessageErrorMessage(
            t("enroll_form.address.phone.error")
          );
        }
      }

      if (formStatus.enrolledInMedicaidQuestionValidity) {
        setEnrolledInMedicaidErrorMessage(undefined);
      } else {
        setEnrolledInMedicaidErrorMessage(
          t("enroll_form.click_button_to_choose_option")
        );
      }
    }
  };

  const submit = (): void => {
    setSubmitted(true);

    if (isValidForm) {
      nextPage();
    } else {
      validateForm();
    }
  };

  useEffect(() => {
    if (submitted && !isValidForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  useEffect(() => {
    validateForm();
  }, [enrollData, submitted]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.general_questions")}
      />

      <PlanInfo plan={plan} />

      <span className="sr-only">
        {t("sr_only_additional_questions_may_appear")}
      </span>

      <div ref={alertDiv}>
        {!isValidForm && submitted && (
          <Alert
            className="ds-u-margin-top--2"
            heading={t("enroll_form.needs_your_attention")}
            variation="error"
          >
            {t("enroll_form.complete_required")}
          </Alert>
        )}
      </div>

      <section>
        {(!isPDP || isCP_PD || isPFFS_PD) && (
          <ChoiceList
            type="radio"
            label={
              <h3 className="ds-u-text-transform--none">
                {t("enroll_form.renal_disease_question")}
              </h3>
            }
            className="ds-u-margin-top--5"
            name="endStageRenalDisease"
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.renalDisease,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.renalDisease === false,
              },
            ]}
            errorMessage={renalDiseaseErrorMessage}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                renalDisease: e.target.value === "yes",
              });
            }}
          />
        )}

        {(plan.plan_type !== PlanType.MA ||
          plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) && (
          <>
            <ChoiceList
              name="additionalDrugCoverage"
              type="radio"
              choices={[
                {
                  label: t("yes"),
                  value: "yes",
                  checked: enrollData.hasAdditionalDrugCoverage,
                },
                {
                  label: t("no"),
                  value: "no",
                  checked: enrollData.hasAdditionalDrugCoverage === false,
                },
              ]}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  hasAdditionalDrugCoverage: e.target.value === "yes",
                  additionalDrugCoverage: {
                    name: "",
                    id: "",
                    group: "",
                  },
                });
              }}
              label={
                <h3 className="ds-u-text-transform--none">{`${t(
                  "enroll_form.additional_drug_coverage_question"
                )} ${t("enroll_form.optional")}`}</h3>
              }
              hint={t("enroll_form.additional_drug_coverage_info")}
            />

            {enrollData.hasAdditionalDrugCoverage && (
              <>
                <TextField
                  name="additionalDrugCoverageName"
                  label={t("enroll_form.additional_drug_coverage.name")}
                  value={enrollData.additionalDrugCoverage.name}
                  errorMessage={additionalDrugCoverageNameErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        name: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        name: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />
                <TextField
                  name="additionalDrugCoverageId"
                  label={t("enroll_form.additional_drug_coverage.id")}
                  value={enrollData.additionalDrugCoverage.id}
                  errorMessage={additionalDrugCoverageIdErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        id: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        id: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />
                <TextField
                  name="additionalDrugCoverageGroup"
                  label={t("enroll_form.additional_drug_coverage.group")}
                  value={enrollData.additionalDrugCoverage.group}
                  errorMessage={additionalDrugCoverageGroupErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        group: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        group: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />
              </>
            )}
          </>
        )}

        <ChoiceList
          name="longTermCare"
          type="radio"
          choices={[
            {
              label: t("yes"),
              value: "yes",
              checked: enrollData.inLongTermCareFacility,
            },
            {
              label: t("no"),
              value: "no",
              checked: enrollData.inLongTermCareFacility === false,
            },
          ]}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              inLongTermCareFacility: e.target.value === "yes",
              longTermCareFacility: {
                name: "",
                addressLine1: "",
                city: "",
                state: "",
                zip: "",
                phone: {
                  areaCode: "",
                  exchangeCode: "",
                  lineNumber: "",
                },
              },
            });
          }}
          label={
            <h3 className="ds-u-text-transform--none">{`${t(
              "enroll_form.long_term_care_question"
            )} ${t("enroll_form.optional")}`}</h3>
          }
        />

        {enrollData.inLongTermCareFacility && (
          <>
            <TextField
              name="longTermCareName"
              label={t("enroll_form.long_term_care_facility.name")}
              value={enrollData.longTermCareFacility.name}
              errorMessage={longTermCareNameErrorMessage}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    name: e.target.value,
                  },
                });
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    name: removeExtraWhiteSpace(e.target.value),
                  },
                });
              }}
            />
            <TextField
              name="longTermCareAddressLine1"
              label={t("enroll_form.address.line_1")}
              value={enrollData.longTermCareFacility.addressLine1}
              errorMessage={longTermCareAddressLine1ErrorMessage}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    addressLine1: e.target.value,
                  },
                });
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    addressLine1: removeExtraWhiteSpace(e.target.value),
                  },
                });
              }}
            />
            <TextField
              name="longTermCareCity"
              label={t("enroll_form.address.city")}
              value={enrollData.longTermCareFacility.city}
              errorMessage={longTermCareCityErrorMessage}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    city: e.target.value,
                  },
                });
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    city: removeExtraWhiteSpace(e.target.value),
                  },
                });
              }}
            />
            <Dropdown
              options={[{ name: "", fipsCode: "" }, ...states].map(state => {
                return { label: state.name, value: state.fipsCode };
              })}
              value={
                enrollData.longTermCareFacility
                  ? enrollData.longTermCareFacility.state
                  : ""
              }
              errorMessage={longTermCareStateErrorMessage}
              label={t("enroll_form.address.state")}
              name="longTermCareState"
              onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    state: e.currentTarget.value,
                  },
                });
              }}
            />
            <TextField
              name="longTermCareZip"
              label={t("enroll_form.address.zip")}
              value={enrollData.longTermCareFacility.zip}
              errorMessage={longTermCareZipErrorMessage}
              fieldClassName="ds-c-field--zipCode"
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                limitInputLength("zipCode", 5);
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    zip: e.target.value,
                  },
                });
              }}
            />
            <PhoneNumberInput
              phoneNumber={enrollData.longTermCareFacility.phone}
              name="longTermCarePhone"
              errorMessage={longTermCarePhoneErrorMessage}
              label={t("enroll_form.phone_number")}
              onChange={(longTermCarePhone): void => {
                setEnrollData({
                  ...enrollData,
                  longTermCareFacility: {
                    ...enrollData.longTermCareFacility,
                    phone: longTermCarePhone,
                  },
                });
              }}
            />
          </>
        )}

        {(!isPDP || isCP_PD || isPFFS_PD) && (
          <ChoiceList
            type="radio"
            label={
              <h3 className="ds-u-text-transform--none">
                {t("enroll_form.are_you_enrolled_in_medicaid")}
              </h3>
            }
            labelClassName="questionHeading"
            className="ds-u-margin-top--5"
            name="enrolledInMedicaid"
            errorMessage={enrolledInMedicaidErrorMessage}
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.enrolledInMedicaid,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.enrolledInMedicaid === false,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                enrolledInMedicaid: e.target.value === "yes",
              });
            }}
          />
        )}

        {((!isPDP && !isCP_PD) || plan.category === PlanCategoryType.PFFS) && (
          <ChoiceList
            type="radio"
            label={
              <h3 className="ds-u-text-transform--none">{`${t(
                "enroll_form.health_coverage_through_employer_question"
              )} ${t("enroll_form.optional")}`}</h3>
            }
            labelClassName="questionHeading"
            className="ds-u-margin-top--5"
            name="coverageThroughEmployer"
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.employerHealthCoverage,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.employerHealthCoverage === false,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                employerHealthCoverage: e.target.value === "yes",
              });
            }}
          />
        )}

        {plan.plan_type !== PlanType.PDP && (
          <TextField
            name="primaryCareName"
            label={`${t("enroll_form.primary_care_name")} ${t(
              "enroll_form.optional"
            )}`}
            value={enrollData.primaryCareName}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                primaryCareName: e.target.value,
              });
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                primaryCareName: removeExtraWhiteSpace(e.target.value),
              });
            }}
          />
        )}
      </section>

      <BackNextButtons
        inReview={inReview}
        nextPage={submit}
        previousPage={previousPage}
      />
    </div>
  );
};

export default GeneralQuestions;
